import useAppTheme from 'hooks/shared/useAppTheme';
import moment from 'moment';
import numeral from 'numeral';
import { Color } from 'presentation/theme/colorScheme';
import { FC, PropsWithChildren, ReactNode } from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Formatter } from 'recharts/types/component/DefaultTooltipContent';
import { Margin } from 'recharts/types/util/types';

interface Props extends PropsWithChildren {
  chartData: ChartPoint[];
  chartHeight: number;
  xAxisDateFormat?: string;
  tooltipDateFormat?: string;
  tooltipFormatter: Formatter<string, string>;
  xAxis?: ReactNode;
  yAxisTickFormatter?: (value: any, index: number) => string;
  showXAxis?: boolean;
  showYAxis?: boolean;
  yAxisWidth?: number;
  areaFill?: string;
  lineColor?: Color;
  margin?: Margin;
}

const AppAreaChart: FC<Props> = ({
  chartData,
  chartHeight,
  xAxisDateFormat = 'll',
  tooltipDateFormat = 'll',
  tooltipFormatter,
  xAxis,
  yAxisTickFormatter,
  showXAxis = false,
  showYAxis = false,
  yAxisWidth = 75,
  areaFill,
  lineColor,
  margin,
  children,
}) => {
  const dateFormatter = (date: Date, format: string = 'll'): string =>
    moment(date).format(format);

  const theme = useAppTheme();

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <AreaChart data={chartData} margin={margin}>
        <Tooltip
          itemStyle={{
            color: theme.colors[Color.Primary500],
          }}
          formatter={tooltipFormatter}
          labelFormatter={(date) => dateFormatter(date, tooltipDateFormat)}
        />
        {xAxis ?? (
          <XAxis
            tickLine={false}
            dataKey="date"
            axisLine={{ stroke: theme.colors[Color.Shade100], strokeWidth: 2 }}
            tickFormatter={(value) => dateFormatter(value, xAxisDateFormat)}
            minTickGap={100}
            tickMargin={15}
            hide={!showXAxis}
          />
        )}
        <YAxis
          axisLine={false}
          orientation="right"
          scale="linear"
          tickLine={false}
          tickSize={0}
          width={yAxisWidth}
          hide={!showYAxis}
          tickMargin={10}
          tickFormatter={(value, index) => {
            if (yAxisTickFormatter) {
              return yAxisTickFormatter(value, index);
            }

            return numeral(value).format('0.0a');
          }}
          domain={([dataMin, dataMax]) => {
            const diff = dataMax - dataMin;
            const extraSpace = Math.abs(diff * 0.1);

            return [dataMin - extraSpace, dataMax + extraSpace];
          }}
        />
        <defs>
          <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor={theme.colors[Color.Primary500]}
              stopOpacity={0.4}
            />
            <stop
              offset="95%"
              stopColor={theme.colors[Color.Primary500]}
              stopOpacity={0}
            />
          </linearGradient>
        </defs>
        <Area
          dataKey="price"
          stroke={theme.colors[lineColor ?? Color.Primary500]}
          strokeWidth={3}
          dot={false}
          fill={areaFill ?? 'url(#chartGradient)'}
          type="monotone"
        />
        {children}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default AppAreaChart;
